import React, { useState } from 'react';
import { Input } from 'antd';
import type { InputProps, InputRef } from 'antd';
import type { PasswordProps } from 'antd/es/input';

interface FloatingLabelInputProps extends InputProps {
  label: string;
  password?: boolean;
}

/**
 * Pill-shaped input whose label lives inside the field: centered like a
 * placeholder at rest, floating up to a small caption once focused or
 * filled. antd's Input has no such mode, so this wraps it and derives the
 * floated state from focus + the Form-controlled `value` prop rather than
 * CSS `:placeholder-shown` (Input.Password's affix wrapper breaks that
 * sibling-selector trick).
 *
 * forwardRef matters here beyond the general antd convention: passed as
 * AutoComplete's customizeInput child (see FloatingLabelAutoComplete),
 * rc-select attaches a ref to manage focus/click-outside itself -- without
 * forwarding it through to the underlying Input, that ref resolved to null
 * and threw ("Cannot read properties of null (reading 'focus')") the
 * moment a suggestion was picked.
 */
const FloatingLabelInput = React.forwardRef<InputRef, FloatingLabelInputProps>(
  ({ label, password, value, onFocus, onBlur, className, ...rest }, ref) => {
    const [focused, setFocused] = useState(false);
    const floated = focused || (value !== undefined && value !== null && value !== '');

    const Component = password ? Input.Password : Input;
    const componentProps = password
      ? { ...rest, visibilityToggle: (rest as PasswordProps).visibilityToggle ?? true }
      : rest;

    return (
      <div className={`floating-label-field${floated ? ' floating-label-field--floated' : ''}`}>
        <Component
          {...componentProps}
          ref={ref}
          value={value}
          placeholder=" "
          className={`floating-label-input${className ? ` ${className}` : ''}`}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            onBlur?.(e);
          }}
        />
        <label className="floating-label">{label}</label>
      </div>
    );
  }
);

FloatingLabelInput.displayName = 'FloatingLabelInput';

export default FloatingLabelInput;
